import React, { useEffect, useMemo, useRef, useState } from 'react';

function useInView(
  options = { threshold: 0.4, rootMargin: '0px 0px -20% 0px' }
) {
  const ref = useRef(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return (
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
    );
  }, []);

  const [inView, setInView] = useState(prefersReducedMotion);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        obs.disconnect();
      }
    }, options);

    obs.observe(el);
    return () => obs.disconnect();
  }, [prefersReducedMotion, options]);

  return { ref, inView };
}

const BeschreibungSection = () => {
  const triggerOptions = { threshold: 0.4, rootMargin: '-25% 0px 0px 0px' }; // unteres Viertel

const { ref: headRef, inView: headIn } = useInView(triggerOptions);
const { ref: subRef, inView: subIn } = useInView(triggerOptions);
const { ref: blkRef, inView: blkIn } = useInView(triggerOptions);

  return (
    <section
      id="about"
      data-bg="light"
      aria-label="Haltung und Prinzip"
      className="relative overflow-hidden"
    >
      {/* Übergang aus dem Hero: oben dunkel -> unten hell */}
      <div className="absolute inset-0 bg-gradient-to-b from-eos-grauDunkel via-eos-grauDunkel/25 to-eos-weißFast" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="max-w-3xl">
          {/* Headline bewusst ohne Animation (Entscheidung = ruhig) */}
          <h2
            ref={headRef}
            style={{ transitionDuration: '1000ms' }}
            className={[
              'text-eos-grauDunkel font-semibold tracking-tight leading-[1.08]',
              'text-3xl sm:text-4xl lg:text-5xl',
              'transition-all ease-out',
              headIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
            ].join(' ')}
          >
            Systemdenken als Prinzip.
          </h2>

          <div
            style={{ transitionDuration: '1000ms' }}
            className={[
              'mt-6 h-px w-24 bg-eos-grauDunkel/20',
              'transition-all ease-out',
              headIn ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-75',
            ].join(' ')}
          />

          {/* Subline: wie Hero, sehr ruhig */}
          <p
            ref={subRef}
            style={{ transitionDuration: '1200ms' }}
            className={[
              'mt-6 text-base sm:text-lg text-eos-grauDunkel/75 leading-relaxed',
              'transition-all ease-out',
              subIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
            ].join(' ')}
          >
            Wir entwickeln elektrotechnische Anlagen als zusammenhängendes
            System. Entscheidungen werden vorbereitet, Schnittstellen werden
            geklärt, der Aufbau bleibt nachvollziehbar.
          </p>
        </div>

        {/* Drei Blöcke: Stagger von unten, in Reihenfolge */}
        <div
          ref={blkRef}
          className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12"
        >
          {/* 1 */}
          <div
            style={{ transitionDuration: '900ms', transitionDelay: '0ms' }}
            className={[
              'lg:col-span-4 transition-all ease-out',
              blkIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
            ].join(' ')}
          >
            <div className="mt-2 text-xl font-semibold text-eos-grauDunkel">
              Planung vor Montage.
            </div>
            <div className="mt-4 text-eos-grauDunkel/75 leading-relaxed">
              Anforderungen, Abhängigkeiten und Randbedingungen werden zuerst
              analysiert.
            </div>
            <div className="mt-4 text-eos-grauDunkel/75 leading-relaxed">
              Konsequenz: klare Entscheidungen, sauberer Aufbau, weniger
              Nacharbeit.
            </div>
          </div>

          {/* 2 */}
          <div
            style={{ transitionDuration: '900ms', transitionDelay: '200ms' }}
            className={[
              'lg:col-span-4 transition-all ease-out',
              blkIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
            ].join(' ')}
          >
            <div className="mt-2 text-xl font-semibold text-eos-grauDunkel">
              System vor Einzellösung.
            </div>
            <div className="mt-4 text-eos-grauDunkel/75 leading-relaxed">
              Erzeugung, Verteilung, Steuerung und Sicherheit werden gemeinsam
              gedacht – nicht nacheinander ergänzt.
            </div>
            <div className="mt-4 text-eos-grauDunkel/75 leading-relaxed">
              Konsequenz: ein System, das stabil läuft und sinnvoll erweiterbar
              bleibt.
            </div>
          </div>

          {/* 3 */}
          <div
            style={{ transitionDuration: '900ms', transitionDelay: '200ms' }}
            className={[
              'lg:col-span-4 transition-all ease-out',
              blkIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3',
            ].join(' ')}
          >
            <div className="mt-2 text-xl font-semibold text-eos-grauDunkel">
              Verantwortung endet nicht bei Übergabe.
            </div>
            <div className="mt-4 text-eos-grauDunkel/75 leading-relaxed">
              Inbetriebnahme bedeutet Prüfung, Dokumentation und Einweisung –
              nicht nur „einschalten“.
            </div>
            <div className="mt-4 text-eos-grauDunkel/75 leading-relaxed">
              Konsequenz: das System ist verstanden und bleibt betreibbar.
            </div>
          </div>
        </div>

        <div className="mt-14 lg:mt-16 h-px w-full bg-eos-grauDunkel/10" />
      </div>
    </section>
  );
};

export default BeschreibungSection;
